import {
    ApiMethodRegistrationOptions,
    assertDefined, Evaluable, evaluateWhenFunction,
    ExtractApiMethodParams,
    OmitStrict,
    PartiallyOptional,
    RestfulApiHttpMethod,
    RestfulApiManager, restfulApiParamOptional,
    restfulApiParamRequired,
    RestfulApiParamType,
    stringEnsurePrefix,
    stringTrimSuffix,
    Values
} from '@wix/devzai-utils-common';
import {IDoppeViewerApi} from './doppe-viewer-api';

export const DoppeViewerApiRoutesManagerThrottlingKey = {
    IpAddress: 'ipAddress'
} as const;

export namespace DoppeViewerApiRoutesManager {

    export type ThrottlingKey = Values<typeof DoppeViewerApiRoutesManagerThrottlingKey>;

    export interface ThrottlingKeyContext<PARAMS> {
        params: PARAMS;
        ipAddress: string;
        userId: string;
    }

    export interface ThrottlingConfig<PARAMS> {
        description?: string;
        maxInvocations: number;
        durationWindowMs: number;
        minDifferenceMs?: number;
        throttlingKey: ThrottlingKey | ((context: ThrottlingKeyContext<PARAMS>) => string)
    }

    export interface EndpointMetadata<PARAMS> {
        outputCacheAge?: number;
        clientCacheAge?: number;
        throttling?: ThrottlingConfig<PARAMS> | ThrottlingConfig<PARAMS>[];
        disabled: boolean;
        methodName: string;
    }

    export type MethodRegistrationInfo<METHOD_NAME extends keyof IDoppeViewerApi> =
        ApiMethodRegistrationOptions<IDoppeViewerApi, METHOD_NAME>
        & EndpointMetadata<ExtractApiMethodParams<IDoppeViewerApi, METHOD_NAME>>;

}

export class DoppeViewerApiRoutesManager extends RestfulApiManager<IDoppeViewerApi> {

    private methodsMetadataMap = new Map<keyof IDoppeViewerApi, DoppeViewerApiRoutesManager.MethodRegistrationInfo<any>>();
    private pathToMethodNameMap = new Map<string, string>();

    constructor(
        options: {
            basePath: string;
        }
    ) {
        super({
            basePath: options.basePath
        });

        this.initialize();
    }

    public cloneWithDifferentBasePath (basePath: Evaluable<(prevPath: string) => string>) {
        return new DoppeViewerApiRoutesManager({
            basePath: evaluateWhenFunction(basePath, this.getBasePath())
        })
    }

    public getMethodMetadata(methodName: keyof IDoppeViewerApi) {
        return assertDefined(this.methodsMetadataMap.get(methodName));
    }

    public registerApiMethod<METHOD_NAME extends keyof IDoppeViewerApi>(
        apiMethodName: METHOD_NAME,
        options: PartiallyOptional<OmitStrict<DoppeViewerApiRoutesManager.MethodRegistrationInfo<METHOD_NAME>, 'methodName'>, 'disabled'>
    ) {
        const {
            disabled = false,
            outputCacheAge,
            clientCacheAge,
            throttling,
            ...apiMethodRegistrationOptions
        } = options;

        if ((outputCacheAge !== undefined || clientCacheAge !== undefined) && apiMethodRegistrationOptions.method !== RestfulApiHttpMethod.Get) {
            throw new Error(`outputCacheAge and clientCacheAge can be provided only for endpoints with http GET method`)
        }

        const apiMethodPath =
            stringTrimSuffix(stringEnsurePrefix(apiMethodRegistrationOptions.path, '/'), '/');
        if (this.pathToMethodNameMap.has(apiMethodPath)) {
            throw new Error(`Several apiMethods are registered for path '${apiMethodPath}' ('${apiMethodName}' and '${this.pathToMethodNameMap.get(apiMethodPath)}')`);
        } else {
            this.pathToMethodNameMap.set(apiMethodPath, apiMethodName);
        }

        this.methodsMetadataMap.set(apiMethodName, {
            ...options,
            methodName: apiMethodName,
            disabled: disabled
        } as DoppeViewerApiRoutesManager.MethodRegistrationInfo<any>)

        super.registerApiMethod<METHOD_NAME>(apiMethodName, apiMethodRegistrationOptions);
    }

    public getRegisteredMethods() {
        return [...this.methodsMetadataMap.values()];
    }

    private initialize() {

        this.registerApiMethod('health', {
            path: '/health',
            params: {},
            method: RestfulApiHttpMethod.Get
        });

        this.registerApiMethod('reportAnalyticsEvent', {
            path: '/analytics/report',
            params: {
                eventName: restfulApiParamRequired(RestfulApiParamType.Body),
                eventParams: restfulApiParamRequired(RestfulApiParamType.Body)
            },
            method: RestfulApiHttpMethod.Post,
            useBeacon: true
        });

        this.registerApiMethod('reportAnalyticsEventBulk', {
            path: '/analytics/reportBulk',
            params: {
                events: restfulApiParamRequired(RestfulApiParamType.Body)
            },
            method: RestfulApiHttpMethod.Post,
            useBeacon: true
        });

        this.registerApiMethod('addContactV2', {
            path: '/contacts/addV2',
            params: {
                email: restfulApiParamOptional(RestfulApiParamType.Body),
                emailSubscription: restfulApiParamOptional(RestfulApiParamType.Body),
                phone: restfulApiParamOptional(RestfulApiParamType.Body),
                contactName: restfulApiParamOptional(RestfulApiParamType.Body),
                address: restfulApiParamOptional(RestfulApiParamType.Body),
                zipcode: restfulApiParamOptional(RestfulApiParamType.Body),
                actionId: restfulApiParamRequired(RestfulApiParamType.Body),
                linkId: restfulApiParamRequired(RestfulApiParamType.Body),
                pageId: restfulApiParamRequired(RestfulApiParamType.Body),
                targetUrl: restfulApiParamRequired(RestfulApiParamType.Body),
                message: restfulApiParamOptional(RestfulApiParamType.Body),
            },
            method: RestfulApiHttpMethod.Post
        });

        this.registerApiMethod('unlockAction', {
            path: '/actions/unlockAction',
            params: {
                actionId: restfulApiParamRequired(RestfulApiParamType.Body),
                pageId: restfulApiParamRequired(RestfulApiParamType.Body),
                unlockedMetaData: restfulApiParamOptional(RestfulApiParamType.Body)
            },
            method: RestfulApiHttpMethod.Post
        });

        this.registerApiMethod('reportEventToWixAutomations', {
            path: '/report-event',
            params: {
                trigger: restfulApiParamRequired(RestfulApiParamType.Body)
            },
            method: RestfulApiHttpMethod.Post
        });

        this.registerApiMethod('submitCodeToUnlock', {
            path: '/unlock-code',
            params: {
                code: restfulApiParamRequired(RestfulApiParamType.Body),
                actionId: restfulApiParamRequired(RestfulApiParamType.Body),
                linkId: restfulApiParamRequired(RestfulApiParamType.Body),
                pageId: restfulApiParamRequired(RestfulApiParamType.Body),
                targetUrl: restfulApiParamRequired(RestfulApiParamType.Body),
            },
            method: RestfulApiHttpMethod.Post
        });




        this.registerApiMethod('createStripePaymentIntent', {
            path: '/stripe/create-payment-intent',
            params: {
                checkoutIntent: restfulApiParamRequired(RestfulApiParamType.Body),
                statementDescriptor: restfulApiParamOptional(RestfulApiParamType.Body),
                idempotencyKey: restfulApiParamOptional(RestfulApiParamType.Body),
                metadata: restfulApiParamRequired(RestfulApiParamType.Body),
            },
            method: RestfulApiHttpMethod.Post
        });

        this.registerApiMethod('saveCheckoutIntent', {
            path: '/checkout-intent/save',
            params: {
                checkoutIntent: restfulApiParamRequired(RestfulApiParamType.Body),
                paymentIntentId: restfulApiParamRequired(RestfulApiParamType.Body),
            },
            method: RestfulApiHttpMethod.Post
        });

        this.registerApiMethod('createWixStoresCheckoutUrl', {
            path: '/wix-stores/create-checkout-url',
            params: {
                productId: restfulApiParamRequired(RestfulApiParamType.Body),
                productProperties: restfulApiParamOptional(RestfulApiParamType.Body),
                variantId: restfulApiParamOptional(RestfulApiParamType.Body),
                customTextFields: restfulApiParamOptional(RestfulApiParamType.Body),
                quantity: restfulApiParamOptional(RestfulApiParamType.Body),
                returnUrl: restfulApiParamRequired(RestfulApiParamType.Body),
            },
            method: RestfulApiHttpMethod.Post
        });

        this.registerApiMethod('updateStripePaymentIntent', {
            path: '/stripe/update-payment-intent',
            params: {
                paymentIntentId: restfulApiParamRequired(RestfulApiParamType.Body),
                updateParams: restfulApiParamRequired(RestfulApiParamType.Body),
            },
            method: RestfulApiHttpMethod.Post
        });

        this.registerApiMethod('getAllSearchLinks', {
            path: '/search-links',
            params: {},
            method: RestfulApiHttpMethod.Get
        });

        this.registerApiMethod('getStripeAccountId', {
            path: '/stripe/get-account-id',
            params: {},
            method: RestfulApiHttpMethod.Get
        });

        this.registerApiMethod('notifyDonateSuccess', {
            path: '/donate/success',
            params: {
                paymentIntentId: restfulApiParamRequired(RestfulApiParamType.Body),
                uouMessage: restfulApiParamOptional(RestfulApiParamType.Body),
                emailSubscriptionConsent: restfulApiParamOptional(RestfulApiParamType.Body),
            },
            method: RestfulApiHttpMethod.Post
        })

        this.registerApiMethod('notifyRequestSuccess', {
            path: '/request/success',
            params: {
                paymentIntentId: restfulApiParamRequired(RestfulApiParamType.Body),
                uouMessage: restfulApiParamOptional(RestfulApiParamType.Body),
                emailSubscriptionConsent: restfulApiParamOptional(RestfulApiParamType.Body),
            },
            method: RestfulApiHttpMethod.Post
        })

        this.registerApiMethod('notifyPaidMessageSuccess', {
            path: '/paid-message/success',
            params: {
                paymentIntentId: restfulApiParamRequired(RestfulApiParamType.Body),
                uouMessage: restfulApiParamRequired(RestfulApiParamType.Body),
                emailSubscriptionConsent: restfulApiParamOptional(RestfulApiParamType.Body),
            },
            method: RestfulApiHttpMethod.Post
        })


    }
}
